<script lang="ts" setup>
import { isObject } from "@vue/shared";
import { computed, useSlots } from "vue";
import { useRouter } from "vue-router";
import { openModal, openSlideover } from "../../../../composables/dialog/drawer";
import { DrawerUrlOptions } from "../../../../store/drawers";
import MButton from "../../MButton";
import { ref } from "vue";

const props = withDefaults(
  defineProps<{
    presentationStyle?: "vertical-line" | "bullet";

    to?: string;

    toSlideover?: string | DrawerUrlOptions | object | false;
    toModal?: string | DrawerUrlOptions | object | false;

    onClick?: (() => void) | Array<() => void>;
    onActionClick?: (() => void) | Array<() => void>;

    iconName?: string;

    srActionLabel?: string;
    srLabel?: string;
    iconOnly?: boolean;
    noIcon?: boolean;
  }>(),
  {
    iconName: "fa-light fa-chevron-right",
    iconOnly: true,
    noIcon: false,
  },
);

const emit = defineEmits({
  click: (_e: MouseEvent) => true,
  actionClick: (_e: MouseEvent) => true,
});

const router = useRouter();
const slots = useSlots();

const refActionButton = ref();

const hasActionSlot = computed(() => !!slots.action);

const conditionalAttributes = computed(() => {
  const attributes: Record<string, unknown> = {};

  if (props.iconOnly) {
    attributes["iconOnly"] = true;
  } else if (props.noIcon) {
    attributes["icon"] = null;
  }

  return attributes;
});

const click = computed(() => {
  if (props.onClick) {
    return (e: MouseEvent) => {
      emit("click", e);

      if (typeof props.onClick === "function") {
        props.onClick();
      }

      refActionButton.value?.focus();
    };
  }
  if (props.toModal) {
    const [url, options] = isObject(props.toModal)
      ? [props.toModal.url, props.toModal]
      : [props.toModal, {}];
    return (_e: MouseEvent) => {
      refActionButton.value?.focus();
      openModal(url, options);
    };
  }
  if (props.toSlideover) {
    const [url, options] = isObject(props.toSlideover)
      ? [props.toSlideover.url, props.toSlideover]
      : [props.toSlideover, {}];
    return (_e: MouseEvent) => {
      refActionButton.value?.focus();
      openSlideover(url, options);
    };
  }

  if (props.to) {
    return (_e: MouseEvent) => {
      refActionButton.value?.focus();
      router.push(props.to!);
    };
  }
  return undefined;
});

const actionClick = computed(() => {
  if (props.onActionClick) {
    return (e: MouseEvent) => {
      emit("actionClick", e);

      if (typeof props.onActionClick === "function") {
        props.onActionClick();
      }
    };
  }

  return undefined;
});
</script>

<template>
  <li
    class="m-list-item"
    :class="[
      {
        clickable: !!click,
        'action-clickable': !!actionClick,
        'has-action-slot': hasActionSlot,
      },
      presentationStyle,
    ]"
    @click="click"
  >
    <div class="m-list-item--content">
      <slot />
    </div>
    <div v-if="click || actionClick || $slots.action" class="m-list-item--action">
      <slot name="action">
        <m-button
          ref="refActionButton"
          class="m-list-item--action-button"
          :icon="iconName"
          :label="srActionLabel || srLabel"
          :on-click="actionClick ? actionClick : click"
          ghost
          v-bind="conditionalAttributes"
        />
      </slot>
    </div>
  </li>
</template>
<style lang="scss">
.m-list-item {
  display: grid;
  grid-template-areas: "content" "action";
  grid-template-columns: 1fr 0;
  padding: var(--space-1) var(--space-2-5);
  min-height: 20px;

  // ENG-21436 reserve the action column/gap whenever any action renders
  &.clickable,
  &.action-clickable,
  &.has-action-slot {
    grid-template-columns: 1fr auto;
    column-gap: var(--space-1);
  }

  &.clickable {
    cursor: pointer;

    &:hover,
    &.hover-component,
    &:focus-within {
      background-color: var(--grey-lightest);
    }
  }

  &.action-clickable {
    .m-list-item--action {
      cursor: pointer;

      &:hover,
      &.hover-component,
      &:focus-within {
        background-color: var(--grey-lightest);
      }
    }
  }

  .m-list-item--content {
    grid-area: "content";
    word-break: break-word;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--space-1);
    white-space: pre-line;
  }

  .m-list-item--action {
    grid-area: "action";
  }

  .m-list-item--action-button {
    margin-top: var(--space-1);
  }

  &.bullet {
    padding-left: var(--space-6) !important;
    padding-top: var(--space-0-5) !important;
    padding-bottom: var(--space-0-5) !important;

    &::before {
      content: "•";
      position: absolute;
      left: var(--space-4);
    }
  }

  &.vertical-line {
    position: relative;
    padding-left: var(--space-6) !important;

    &::before {
      content: " ";
      position: absolute;
      top: var(--space-1);
      left: var(--space-4);
      height: calc(100% - 10px);
      width: 3px;
      background-color: var(--status-blue);
    }
  }

  .labeled-text {
    &.horizontal {
      // reset margin top
      > .text,
      > .label {
        margin-top: 0;
      }
    }
  }
}
</style>
